import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Search } from 'lucide-react';

import { AccountMenu } from '@/components/AccountMenu';
import { BrandLogo } from '@/components/BrandLogo';
import { HeaderSearch } from '@/components/HeaderSearch';
import { appHeaderNavItems as navItems } from '@/components/appNavItems';
import { useAuth } from '@/hooks/useAuth';
import { useDueTrainingCount } from '@/contexts/DueTrainingCountContext';
import { cn } from '@/services/utils';
import { signInWithBackend } from '@/services';
import { Button } from '@/components/ui/button';

import { MEANING_GRID_COLS_CLASS, MEANING_ROW_MAX_WIDTH_CLASS } from '@/pages/meaning/layoutGrid';

const isRouteActive = (pathname: string, path: string) =>
  pathname === path || pathname.startsWith(`${path}/`);

// Shared desktop chrome for every route that's had its "Card Toolbar Options" redesign applied
// (Meaning, Training) — logo, Meaning/Training nav, search and account, all in one sticky
// bar. Named after the app, not any one page, since it's no longer Meaning-specific.
export function AppTopHeader({
  fullBleed = false,
  maxWidthClassName = MEANING_ROW_MAX_WIDTH_CLASS,
}: {
  /** The card-detail view stretches edge to edge (no side gutters); the list keeps the capped, centered frame. */
  fullBleed?: boolean;
  /** Overrides the capped-centered frame's max-width (ignored when fullBleed). */
  maxWidthClassName?: string;
}) {
  const { pathname } = useLocation();
  const { dueCount } = useDueTrainingCount();
  const { isAuthenticated } = useAuth();
  const [searchOpen, setSearchOpen] = useState(false);

  return (
    <header className="sticky top-0 z-20 flex h-[66px] shrink-0 items-center border-b border-[#ece9e2] bg-white font-[family-name:var(--font-meaning-sans)]">
      {/* fullBleed (card-detail view): the logo stays exactly where the centered list layout would
          put it — same 348px width, offset by the same left gap centering would produce — while the
          nav/search/account area is free to grow all the way to the true right edge instead of
          stopping at the 1364px cap. Implemented as padding-left instead of margin so only the left
          side is pinned; a plain mx-auto would still leave an equal, unwanted gap on the right. */}
      <div
        className={cn(
          'grid w-full flex-1 items-center',
          fullBleed ? 'grid-cols-[348px_minmax(0,1fr)_auto]' : cn('mx-auto', MEANING_GRID_COLS_CLASS, maxWidthClassName)
        )}
        style={fullBleed ? { paddingLeft: 'max(0px, calc((100% - 1364px) / 2))' } : undefined}
      >
        <Link to="/meaning" aria-label="Yawca home" className="box-border flex min-w-0 items-center pl-4">
          <BrandLogo />
        </Link>

        {/* min-w-0 (matching <main>'s own min-w-0 below) keeps this column's width driven purely by
            the fr ratio instead of its content's natural minimum — otherwise the nav+search row's
            min-content would force this track wider than the feed column, throwing off the
            start/end alignment this is meant to guarantee. Same 18px padding as <main>, so the nav
            starts exactly where the cards start and the search control ends exactly where they
            end. */}
        <div className="flex min-w-0 items-center justify-center gap-2 px-[18px]">
          {/* Collapses via grid-template-columns (1fr -> 0fr) rather than display:none, so the nav
              shrinks away smoothly instead of vanishing instantly — clearing the room the expanding
              search bar grows into, all the way to where the menu started. */}
          <div
            className="grid overflow-hidden transition-[grid-template-columns] duration-200 ease-out"
            style={{ gridTemplateColumns: searchOpen ? '0fr' : '1fr' }}
          >
            <nav aria-label="Main navigation" className="flex min-w-0 items-center gap-2 overflow-hidden">
              {navItems.map((item) => {
                const Icon = item.icon;
                const active = isRouteActive(pathname, item.path);
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    state={item.path === '/training' ? { showTrainingModePicker: true } : undefined}
                    className={cn(
                      'flex h-9 items-center gap-1.5 whitespace-nowrap rounded-full px-3 text-base font-semibold transition',
                      active ? 'bg-[#f1efe9] font-extrabold text-[#1e1c18]' : 'text-[#57534a] hover:bg-[#f6f4ef]'
                    )}
                  >
                    <Icon className="size-[19px]" />
                    <span>{item.label}</span>
                    {item.path === '/training' && !!dueCount && (
                      <span className="rounded-full bg-[#2f5c4e] px-1.5 py-px text-[13px] font-bold text-white">
                        {dueCount}
                      </span>
                    )}
                  </Link>
                );
              })}
            </nav>
          </div>

          <div className={cn('flex min-w-0 items-center', searchOpen && 'flex-1')}>
            {searchOpen ? (
              <HeaderSearch onClose={() => setSearchOpen(false)} />
            ) : (
              <div className="flex shrink-0 items-center gap-2">
                <div className="h-6 w-px bg-[#e6e2da]" aria-hidden="true" />
                <button
                  type="button"
                  aria-label="Search words and tags"
                  onClick={() => setSearchOpen(true)}
                  className="flex h-9 items-center gap-1.5 whitespace-nowrap rounded-full px-3 text-base font-semibold text-[#57534a] transition hover:bg-[#f6f4ef]"
                >
                  <Search className="size-[18px]" />
                  <span>Search</span>
                </button>
              </div>
            )}
          </div>
        </div>

        {/* No third grid item anymore — an explicit grid-template-columns track still reserves its
            share of the width even with nothing placed in it, so the logo/nav stay exactly where
            they were. The account menu itself has moved out to the absolutely-positioned block
            below, pinned to the true right edge of the screen instead of the capped frame's edge
            (which sits short of it once the row below goes wider than 1364px in the card-detail
            view). */}
      </div>

      <div className="absolute right-4 top-1/2 flex -translate-y-1/2 items-center gap-2.5">
        {isAuthenticated ? (
          <AccountMenu />
        ) : (
          <Button size="sm" onClick={() => signInWithBackend()}>
            Sign in
          </Button>
        )}
      </div>
    </header>
  );
}
